import React from 'react';
import { graphql, useFragment } from 'react-relay';
import BugIcon from '@atlaskit/icon/core/bug';
import JiraTestSessionIcon from '@atlaskit/icon/glyph/jira/test-session';
import Lozenge from '@atlaskit/lozenge';
import { Text, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type {
	criticalVulnerability_periscope_SecurityVulnerabilityTask$data,
	criticalVulnerability_periscope_SecurityVulnerabilityTask$key,
} from '@atlassian/jira-relay/src/__generated__/criticalVulnerability_periscope_SecurityVulnerabilityTask.graphql';
import { TaskItem } from '../../../common/ui/task-item';
import { TaskTypeItem } from '../../../common/ui/task-type';
import { formatTimeLapse } from '../../../common/utils';
import { useWorkSuggestionsContainer } from '../../../services/periscope-container';
import { useWorkSuggestionsAction } from '../../../services/use-work-suggestions-action';
import { messages } from './messages';

type Props = {
	data: criticalVulnerability_periscope_SecurityVulnerabilityTask$data;
};
const VulnInfo = ({ data }: Props) => {
	const { formatRelativeTime } = useIntl();
	return (
		<Flex gap="space.100" alignItems="center">
			<BugIcon
				LEGACY_fallbackIcon={JiraTestSessionIcon}
				LEGACY_size="medium"
				label="bug icon"
				color={token('color.icon.accent.purple')}
			/>
			<Flex direction="column" gap="space.050">
				<Flex alignItems="center" gap="space.050">
					<Flex>
						<Tooltip content={data.title} position="top">
							<Text color="color.text" maxLines={1}>
								{data.title}
							</Text>
						</Tooltip>
					</Flex>
					<Flex>
						<Lozenge appearance="default">{data.status}</Lozenge>
					</Flex>
				</Flex>
				<Flex gap="space.100">
					<Text>{data.securityContainerName}</Text>
					<Text>•</Text>
					<Text>{data.issueKey}</Text>
					<Text>•</Text>
					<Text>{formatTimeLapse(formatRelativeTime, data.introducedDate)}</Text>
				</Flex>
			</Flex>
		</Flex>
	);
};

export const SecurityVulnerabilityTask = ({
	fragmentRef,
}: {
	fragmentRef: criticalVulnerability_periscope_SecurityVulnerabilityTask$key;
}) => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment criticalVulnerability_periscope_SecurityVulnerabilityTask on WorkSuggestionsCriticalVulnerabilityTask {
				__typename
				id
				url
				title
				issueKey
				securityContainerName
				status
				introducedDate
			}
		`,
		fragmentRef,
	);
	const { snooze, remove } = useWorkSuggestionsAction();
	const [{ showReactionsButtonForSuggestions }, { hideSuggestionsReactionsButton }] =
		useWorkSuggestionsContainer();

	return (
		<TaskTypeItem
			onRemove={() => remove({ id: data.id, typename: data.__typename })}
			onSnooze={() => snooze({ id: data.id, typename: data.__typename })}
			showReactionsButton={
				showReactionsButtonForSuggestions[data.id] === true ||
				showReactionsButtonForSuggestions[data.id] == null
			}
			onReactionsButtonClick={() => hideSuggestionsReactionsButton(data.id)}
			url={data.url}
			suggestionType={data.__typename}
			id={data.id}
			actionButtonTitle={formatMessage(messages.actionButtonNonFinal)}
		>
			<TaskItem taskInfoContent={VulnInfo({ data })} />
		</TaskTypeItem>
	);
};
